import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";

import { iniciarConversacionThunk } from "../../store/conversacion/conversacion.thunks";
import Select2 from "../../components/UI/Select2";
import Loader from "../../components/UI/Loader";
import apiBase from "../../api/axios";

const ConversacionIniciar = ({ puedeIniciar, onSesionIniciada, sesionActiva }) => {
  const dispatch = useDispatch();
  const { iniciando } = useSelector((state) => state.conversacion);

  const [estudianteId, setEstudianteId] = useState(null);
  const [talentoUsuarioId, setTalentoUsuarioId] = useState(null);
  const [conversacionPlantelId, setConversacionPlantelId] = useState(null);

  const fetchEstudiantes = useCallback(async (search) => {
    try {
      const url = search
        ? `v1/seguimiento/mis-alumnos/?search=${encodeURIComponent(search)}`
        : "v1/seguimiento/mis-alumnos/";
      const res = await apiBase.get(url);
      const data = res.data?.results ?? res.data;
      return (Array.isArray(data) ? data : []).map((item) => ({
        id: item.estudiante_id,
        label: item.estudiante_nombre,
      }));
    } catch {
      return [];
    }
  }, []);

  const fetchTalentosUsuario = useCallback(async (search) => {
    if (!estudianteId) return [];
    try {
      let url = `v1/mis-talentos/?usuario_id=${estudianteId}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await apiBase.get(url);
      const optimos = res.data?.talentos_optimos ?? [];
      return optimos.map((item) => ({
        id: item.talento_id,
        label: `#${item.ranking} — ${item.talento_nombre}`,
      }));
    } catch {
      return [];
    }
  }, [estudianteId]);

  const fetchConversaciones = useCallback(async (search) => {
    try {
      const url = search
        ? `v1/talentos-plantel/selectores/conversaciones/?search=${encodeURIComponent(search)}`
        : "v1/talentos-plantel/selectores/conversaciones/";
      const res = await apiBase.get(url);
      const data = res.data?.results ?? res.data;
      return (Array.isArray(data) ? data : []).map((item) => ({
        id: item.id,
        label: item.label || item.pregunta || String(item.id),
      }));
    } catch {
      return [];
    }
  }, []);

  const handleIniciar = useCallback(async () => {
    if (!estudianteId || !talentoUsuarioId || !conversacionPlantelId) {
      toast.warning("Selecciona estudiante, talento y conversación.", {
        position: "top-center",
      });
      return;
    }

    let talentoUsuarioRealId = talentoUsuarioId;
    try {
      const res = await apiBase.get(`v1/mis-talentos/?usuario_id=${estudianteId}`);
      const optimos = res.data?.talentos_optimos ?? [];
      const match = optimos.find((t) => t.talento_id === talentoUsuarioId);
      if (match?.id) talentoUsuarioRealId = match.id;
    } catch { /* fallback */ }

    const result = await dispatch(
      iniciarConversacionThunk({
        estudiante_id: estudianteId,
        talento_usuario_id: talentoUsuarioRealId,
        conversacion_plantel_id: conversacionPlantelId,
      }),
    );
    if (result.type.endsWith("/fulfilled")) {
      toast.success("¡Sesión iniciada exitosamente!", { position: "top-center" });
      onSesionIniciada();
    } else {
      toast.error(result.payload || "Error al iniciar la sesión", { position: "top-center" });
    }
  }, [estudianteId, talentoUsuarioId, conversacionPlantelId, dispatch, onSesionIniciada]);

  if (sesionActiva && sesionActiva.estado === "en_progreso") {
    return (
      <div className="conversacion-card card border-success" style={{ borderLeftWidth: 4 }}>
        <div className="card-body p-4 p-lg-5">
          <div className="d-flex align-items-center gap-3 mb-4">
            <div className="conversacion-icon-wrapper bg-success-subtle" style={{ width: 52, height: 52, borderRadius: 14 }}>
              <Icon icon="mdi:forum" width="26" className="text-success" />
            </div>
            <div>
              <h5 className="mb-1">Sesión activa</h5>
              <p className="text-secondary mb-0" style={{ fontSize: "0.9rem" }}>
                Tienes una conversación en curso con tu estudiante.
              </p>
            </div>
          </div>
          <div className="row g-3">
            <div className="col-6 col-md-3">
              <div className="conversacion-stat">
                <span className="conversacion-stat__label">Talento</span>
                <span className="conversacion-stat__value">{sesionActiva.talento_nombre}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="conversacion-stat">
                <span className="conversacion-stat__label">Conversación</span>
                <span className="conversacion-stat__value">#{sesionActiva.conversacion_numero}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="conversacion-stat">
                <span className="conversacion-stat__label">Estudiante</span>
                <span className="conversacion-stat__value">{sesionActiva.estudiante_nombre}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="conversacion-stat">
                <span className="conversacion-stat__label">Respuestas</span>
                <span className="conversacion-stat__value">{sesionActiva.cantidad_respuestas}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!puedeIniciar) {
    return (
      <div className="conversacion-card card text-center">
        <div className="card-body p-5">
          <div className="conversacion-empty-state">
            <div className="conversacion-icon-wrapper bg-primary-subtle bg-opacity-50 mx-auto mb-4" style={{ width: 72, height: 72, borderRadius: 20 }}>
              <Icon icon="mdi:account-school-outline" width="36" className="text-primary" />
            </div>
            <h5 className="mb-2">No tienes sesiones activas</h5>
            <p className="text-secondary mx-auto" style={{ maxWidth: 420, fontSize: "0.925rem" }}>
              Espera a que tu mentor inicie una conversación contigo para comenzar a desarrollar tus talentos.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (iniciando) {
    return <Loader center text="Iniciando sesión..." delay={200} />;
  }

  return (
    <div className="conversacion-card card">
      <div className="card-body p-4 p-lg-5">
        <div className="d-flex align-items-center gap-3 mb-4">
          <div className="conversacion-icon-wrapper bg-primary-subtle" style={{ width: 52, height: 52, borderRadius: 14 }}>
            <Icon icon="mdi:play-circle" width="26" className="text-primary" />
          </div>
          <div>
            <h5 className="mb-1">Iniciar nueva conversación</h5>
            <p className="text-secondary mb-0" style={{ fontSize: "0.9rem" }}>
              Selecciona el estudiante, el talento y la conversación para comenzar.
            </p>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-md-4">
            <label className="form-label fw-semibold mb-2">
              <Icon icon="mdi:account-circle" className="me-2" width="18" />
              Estudiante
            </label>
            <Select2
              fetchOptions={fetchEstudiantes}
              value={estudianteId}
              onChange={(val) => setEstudianteId(val || null)}
              placeholder="Buscar estudiante por nombre..."
            />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold mb-2">
              <Icon icon="mdi:star-circle" className="me-2" width="18" />
              Talento
            </label>
            <Select2
              fetchOptions={fetchTalentosUsuario}
              value={talentoUsuarioId}
              onChange={(val) => setTalentoUsuarioId(val || null)}
              placeholder={estudianteId ? "Elegir talento..." : "Primero elige un estudiante"}
              disabled={!estudianteId}
            />
            {estudianteId && (
              <small className="text-muted mt-1 d-block">
                <Icon icon="mdi:information-outline" width="14" className="me-1" />
                Se muestran los talentos óptimos del estudiante
              </small>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold mb-2">
              <Icon icon="mdi:forum-outline" className="me-2" width="18" />
              Conversación
            </label>
            <Select2
              fetchOptions={fetchConversaciones}
              value={conversacionPlantelId}
              onChange={(val) => setConversacionPlantelId(val || null)}
              placeholder="Seleccionar (1-5)..."
            />
          </div>
        </div>

        <div className="d-flex justify-content-end mt-4 pt-2">
          <button
            className="btn btn-primary px-5 py-2"
            onClick={handleIniciar}
            disabled={!estudianteId || !talentoUsuarioId || !conversacionPlantelId}
          >
            <Icon icon="mdi:play-circle-outline" className="me-2" width="20" />
            Iniciar conversación
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversacionIniciar;
